import React, { useContext, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import SidebarBest from './Sidebar/SidebarBest';
import MobileBottomNav from './Sidebar/MobileBottomNav';
import HeaderNew from './HeaderNew';
import { Outlet } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { InVenueUsersProvider } from '../context/useInVenueUsers';
import { getVenueTypeConfig } from '../lib/consts/venueTypes';

const MD_BREAKPOINT_PX = 768;

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bottomBarVisible, setBottomBarVisible] = useState(true);
  const location = useLocation();
  const [token, , , venueID, , , , , , , , venueType] = useContext(UserContext);
  const venueConfig = getVenueTypeConfig(venueType);
  const isAssistantChatPage =
    location.pathname === '/asistente-ia' || location.pathname.endsWith('/asistente-ia');
  const mainRef = useRef(null);
  const lastScrollTop = useRef(0);
  const scrollRaf = useRef(null);
  const touchLastY = useRef(null);
  const touchThrottleUntil = useRef(0);

  // Móvil (< md): al bajar el contenido se oculta la barra inferior; al subir, vuelve.
  useEffect(() => {
    lastScrollTop.current = 0;
    setBottomBarVisible(true);
  }, [location.pathname]);

  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;

    const isMobile = () =>
      typeof window.matchMedia === 'function'
        ? window.matchMedia(`(max-width: ${MD_BREAKPOINT_PX - 1}px)`).matches
        : window.innerWidth < MD_BREAKPOINT_PX;

    const isAssistantCompact = () =>
      isAssistantChatPage &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1023px)').matches;

    const runScrollLogic = () => {
      if (isAssistantCompact()) return;
      const st = el.scrollTop;
      if (!isMobile()) {
        setBottomBarVisible(true);
        lastScrollTop.current = st;
        return;
      }

      const delta = st - lastScrollTop.current;
      const threshold = 6;

      if (st < 12) {
        setBottomBarVisible(true);
      } else if (delta > threshold) {
        setBottomBarVisible(false);
      } else if (delta < -threshold) {
        setBottomBarVisible(true);
      }
      lastScrollTop.current = st;
    };

    const onScroll = () => {
      if (scrollRaf.current != null) return;
      scrollRaf.current = requestAnimationFrame(() => {
        scrollRaf.current = null;
        runScrollLogic();
      });
    };

    const onResize = () => {
      if (!isMobile()) setBottomBarVisible(true);
    };

    // iOS / Android: a veces el scroll no dispara eventos fiables en <main>; el dedo sí.
    const onTouchStart = (e) => {
      if (!isMobile() || isAssistantCompact() || !e.touches?.length) return;
      touchLastY.current = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      if (!isMobile() || isAssistantCompact() || touchLastY.current == null || !e.touches?.length) return;

      const y = e.touches[0].clientY;
      const dy = y - touchLastY.current;
      touchLastY.current = y;

      if (Math.abs(dy) < 8) return;

      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      if (now < touchThrottleUntil.current) return;

      touchThrottleUntil.current = now + 70;

      // Dedo hacia arriba (dy < 0) → contenido baja → ocultar barra
      if (dy < 0) {
        setBottomBarVisible(false);
      } else {
        setBottomBarVisible(true);
      }
    };

    const onTouchEnd = () => {
      touchLastY.current = null;
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    // Captura: funciona aunque un hijo consuma el scroll o el motor sea irregular
    window.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true, capture: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('touchstart', onTouchStart, true);
      window.removeEventListener('touchmove', onTouchMove, true);
      window.removeEventListener('touchend', onTouchEnd, true);
      window.removeEventListener('touchcancel', onTouchEnd, true);
      if (scrollRaf.current != null) cancelAnimationFrame(scrollRaf.current);
    };
  }, [isAssistantChatPage]);

  return (
    <div className={['flex h-[100dvh] min-h-0 max-h-[100dvh] overflow-hidden', venueConfig.appBackgroundClassName].join(' ')}>
      {/* Wrapper reserva hueco en desktop (Chrome no comprime); en móvil w-0, el drawer es fixed */}
      <div className="w-0 flex-shrink-0 md:w-64">
        <SidebarBest
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* min-h-0: el flex hijo puede encoger; main usa flex-1 basis-0 para que el scroll sea aquí */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0">
          <HeaderNew onMenuClick={() => setSidebarOpen(true)} />
        </div>
        <main
          ref={mainRef}
          className={`flex min-h-0 w-full min-w-0 flex-1 basis-0 flex-col transition-[padding-bottom] duration-300 ease-out ${
            isAssistantChatPage
              ? 'h-0 overflow-hidden overscroll-none p-3 max-md:pb-[calc(3.5rem+max(0.25rem,env(safe-area-inset-bottom,0px))+10px)] md:max-lg:pb-3 lg:h-auto lg:p-8 lg:pb-8'
              : 'overflow-auto p-3 sm:p-4 md:p-6 lg:p-8'
          } ${
            !isAssistantChatPage && bottomBarVisible
              ? 'pb-20 sm:pb-20 md:pb-6'
              : !isAssistantChatPage
                ? 'pb-4 sm:pb-4 md:pb-6'
                : ''
          }`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div
            key={`${location.pathname}:${venueType || ""}`}
            className={`flex min-h-0 min-w-0 w-full flex-col${
              isAssistantChatPage ? ' flex h-full min-h-0 flex-1 overflow-hidden max-lg:h-0' : ''
            }`}
          >
            <InVenueUsersProvider token={token} venueID={venueID}>
              <Outlet />
            </InVenueUsersProvider>
          </div>
        </main>
        <MobileBottomNav
          visible={bottomBarVisible}
          onMenuClick={() => setSidebarOpen(true)}
        />
      </div>
    </div>
  );
}
